"use client";

import { useLocationSelector } from "@/hooks/useLocationSelector";
import { useTranslations } from "next-intl";
import type { CustomLocation, MapAreaId } from "@/utils/location";
import { ChevronDownIcon, Loader2Icon, SearchIcon } from "lucide-react";
import { useCallback } from "react";
import {
  BottomSheet,
  BottomSheetContent,
  BottomSheetDescription,
  BottomSheetDragRegion,
  BottomSheetTitle,
  BottomSheetTrigger,
} from "../ui/bottom-sheet";
import { Input } from "../ui/input";
import { SelectorTrigger } from "../ui/selector-trigger";
import { LocationSelectorContent } from "./shared/LocationSelectorContent";
import { LocationSelectorTriggerContent } from "./shared/LocationSelectorTriggerContent";

export type LocationSelectorMobileProps = {
  className?: string;
  currentArea: MapAreaId;
  currentCustomLocation?: CustomLocation | null;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
};

export default function LocationSelectorMobile({
  className,
  currentArea,
  currentCustomLocation,
  isOpen,
  onOpenChange,
}: LocationSelectorMobileProps) {
  const t = useTranslations();

  // Close handler passed to hook - hook calls this after selections
  const closeSelector = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  const {
    currentCountryOptions,
    effectiveCurrentArea,
    effectiveLabel,
    effectiveCustomLocation,
    handleAreaSelect,
    handlePlaceSelect,
    handleUseMyLocation,
    handleClose,
    searchQuery,
    setSearchQuery,
    placeSuggestions,
    isSearching,
    isDebouncing,
    isLoadingDetails,
    isReverseGeocoding,
    geoError,
    isGeoLoading,
    isGeoSupported,
  } = useLocationSelector({
    currentArea,
    currentCustomLocation,
    onClose: closeSelector,
  });

  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      if (newOpen) {
        onOpenChange(true);
      } else {
        handleClose();
      }
    },
    [onOpenChange, handleClose],
  );

  return (
    <BottomSheet open={isOpen} onOpenChange={handleOpenChange}>
      <BottomSheetTrigger asChild className="w-full">
        <SelectorTrigger
          className={className}
          leftAdornment={
            <LocationSelectorTriggerContent
              effectiveCurrentArea={effectiveCurrentArea}
              effectiveCustomLocation={effectiveCustomLocation ?? null}
              hideImageOnError
            />
          }
          rightAdornment={<ChevronDownIcon className="h-4 w-4" />}
        >
          {effectiveLabel}
        </SelectorTrigger>
      </BottomSheetTrigger>
      <BottomSheetContent
        className="max-h-[90vh] flex flex-col"
        onOpenChange={handleOpenChange}
        open={isOpen}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        {/* Header */}
        <BottomSheetDragRegion className="px-4">
          <BottomSheetTitle className="text-center text-lg font-semibold mb-4">
            {t("locationSelector.title")}
          </BottomSheetTitle>
        </BottomSheetDragRegion>
        <BottomSheetDescription className="sr-only">
          {t("locationSelector.description")}
        </BottomSheetDescription>

        {/* Search Input */}
        <div className="px-4 pb-4">
          <div className="relative">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={t("locationSelector.searchPlaceholder")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-12 pl-9 pr-4"
            />
            {(isSearching || isLoadingDetails || isReverseGeocoding) && (
              <Loader2Icon className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground animate-spin" />
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto no-scrollbar pb-6 min-h-[60vh] px-4">
          <LocationSelectorContent
            searchQuery={searchQuery}
            placeSuggestions={placeSuggestions}
            isSearching={isSearching}
            isDebouncing={isDebouncing}
            effectiveCurrentArea={effectiveCurrentArea}
            effectiveCustomLocation={effectiveCustomLocation ?? null}
            currentCountryOptions={currentCountryOptions}
            isGeoSupported={isGeoSupported}
            isGeoLoading={isGeoLoading}
            isReverseGeocoding={isReverseGeocoding}
            geoError={geoError}
            onAreaSelect={handleAreaSelect}
            onPlaceSelect={handlePlaceSelect}
            onUseMyLocation={handleUseMyLocation}
          />
        </div>
      </BottomSheetContent>
    </BottomSheet>
  );
}
